import React, { useCallback, useEffect, useState } from 'react';
import Portal from 'portal/Portal';
import BaseForm from 'containers/Base/BaseForm';
import { getInitialValues } from 'containers/Task/Form/options';
import {
  detailedSectionFields,
  generalSectionFields,
  operationalSectionFields,
} from '../helpers';
import { useSelector } from 'react-redux';
import { getSecurityCompanyServices } from 'reducers/securityCompany';
import {
  getUserAssignedDepartmentList,
  getUserId,
  getFullUserName,
} from 'reducers/users';
import { getSecurityCompanyId } from 'reducers/securityCompany/index';
import { GEOGRAPHICAL_MAP } from 'utils/constants/Map';
import { TASK_STATUS, TASK_EVIDENCE_TYPE } from 'utils/constants/task';
import {
  formatOptions,
  transformTaskFormValues,
} from 'utils/DataTransform/Task/taskForm';
import { convertToUTCTime } from 'utils/time';
import { CircularProgress } from '@material-ui/core';
import { getLoadingState } from 'reducers/ui';
import isEmpty from 'lodash/isEmpty';
import { find } from 'lodash';
import { toJSON } from 'utils/helper/baseTasks';
import PropTypes from 'prop-types';

const PerformTask = ({
  // serviceType,
  taskDetails,
  id,
  defaultValues,
  closeForm,
  view,
  startTaskHandler,
  endTaskHandler,
  completedEvidences,
}) => {
  //--------------usestate Hooks
  const [formValues, setFormValues] = useState({});
  const [showPopup, setShowPopup] = useState(false);
  //--------------------------------------------

  //----------------------------selectors
  const loading = useSelector(state => getLoadingState(state));
  const services = useSelector(state => getSecurityCompanyServices(state));
  const startedByFullName = useSelector(state => getFullUserName(state));
  const startedByID = useSelector(state => getUserId(state));
  const securityCompanyId = useSelector(state => getSecurityCompanyId(state));
  const userAssignedDepartments = useSelector(state =>
    getUserAssignedDepartmentList(state),
  );
  //---------------------------------------------------------------
  const task = taskDetails?.tasks.find(item => item.id === id);

  const initialValues = {
    ...getInitialValues({
      id,
      values: taskDetails,
      defaultValues,
      taskType: task.taskType,
      multiSchedule: false,
    }),
    evidenceCollection: completedEvidences,
  };

  const handleFormValueUpdate = vals => {
    setFormValues(vals);
  };

  const transformValues = values => {
    // map the evidence collection structure to the one accepted at the api
    if (!values.evidence) return values;
    const evCollection = values.evidenceCollection;
    //transformEvidences
    values.evidence = values.evidence.map(m => {
      const id = m.others?.value?.id;
      const ev = evCollection && evCollection.find(f => f.id === id);
      const transformedEv = { ...m };

      if (ev?.completed.value) {
        // if the evidence is completed
        transformedEv.others.value.completed = ev.completed.value;
        transformedEv.others.value.contents = ev.contents.value;
        transformedEv.others.value.timestamp =
          ev.timestamp || transformedEv.others.value.timestamp;
        // if the evidence is of type multi or single option
        if (
          ev?.type === TASK_EVIDENCE_TYPE.OPTION_MULTIPLE.id ||
          ev?.type === TASK_EVIDENCE_TYPE.OPTION_SINGLE.id
        ) {
          transformedEv.textOptions.value = ev.contents.value.options;
        }
        return transformedEv;
      }
      // explicity set the value and completed status incase the evidence is deleted from evCollection
      transformedEv.others.value.completed = false;
      transformedEv.others.value.contents = null;
      transformedEv.others.value.timestamp = null;
      return transformedEv;
    });
    // transform checklist
    values.checkList = values.checkList.map(m => {
      const id = m.others?.value?.id;
      const ev = evCollection && evCollection.find(f => f.id === id);
      const transformedEv = { ...m };

      if (ev?.completed.value) {
        // if the evidence is completed
        transformedEv.others.value.completed = ev.completed.value;
        transformedEv.others.value.contents = ev.completed.value;
        transformedEv.others.value.timestamp = ev.timestamp;
        return transformedEv;
      }
      // explicity set the value and completed status incase the evidence is deleted from evCollection
      transformedEv.others.value.completed = false;
      transformedEv.others.value.contents = null;
      return transformedEv;
    });

    return values;
  };
  const onSubmit = useCallback(() => {
    // the status is still NEW, then it is not started yet, close the form
    if (
      task?.status === TASK_STATUS.NEW.id ||
      task?.status === TASK_STATUS.RESOLVED_COMPLETE.id ||
      task?.status === TASK_STATUS.RESOLVED_INCOMPLETE.id
    ) {
      closeForm();
    } else {
      // if there are evidences and are not completed yet, then set the status to IN_PROGRESS
      const transformedVals = transformTaskFormValues({
        id: task.id,
        values: transformValues(formValues),
        task: taskDetails,
        mobileStatus: TASK_STATUS.IN_PROGRESS.id,
      });
      endTaskHandler({
        data: transformedVals,
      }); // endtask handler can upload any evidences if attached
      closeForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues, task]);

  const checkIfAllEvidencesAreUploaded = useCallback(() => {
    let evidenceCompletedStatus = TASK_STATUS.RESOLVED_COMPLETE.id;

    if (!formValues?.evidenceCollection && isEmpty(formValues?.evidence)) {
      // this means there are no evidences to check and perform
      return evidenceCompletedStatus;
    }

    if (formValues?.evidence && !formValues?.evidenceCollection) {
      // if there are evidences but no evidenceCollection, then it is RESOLVED_INCOMPLETE
      evidenceCompletedStatus = TASK_STATUS.RESOLVED_INCOMPLETE.id;
      return evidenceCompletedStatus;
    }
    // check if all the evidences are filled
    if (
      formValues?.evidence &&
      formValues?.evidenceCollection &&
      formValues.evidenceCollection.length !==
        formValues.evidence.length + formValues.checkList.length
    ) {
      evidenceCompletedStatus = TASK_STATUS.RESOLVED_INCOMPLETE.id;
      return evidenceCompletedStatus;
    } else {
      // if completed key is true for all the evidences, then return RESOLVED_COMPLETE otherwise RESOLVED_INCOMPLETE
      for (
        let index = 0;
        index < formValues.evidenceCollection.length;
        index += 1
      ) {
        const ev = formValues.evidenceCollection[index];
        if (!ev.completed.value) {
          // this means that there is an incomplete evidence, so return RESOLVED_INCOMPLETE
          evidenceCompletedStatus = TASK_STATUS.RESOLVED_INCOMPLETE.id;
          break;
        }
      }
    }

    return evidenceCompletedStatus;
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues]);

  const startTask = useCallback(() => {
    // get the latest data from formik
    const values = { ...formValues };
    // transform the data required to be sent to the server
    const transformedVals = transformTaskFormValues({
      id: values.id,
      values,
      task: taskDetails,
      mobileStatus: TASK_STATUS.IN_PROGRESS.id,
      timeStarted: convertToUTCTime({ date: Date.now() }),
      startedByID: startedByID,
      startedByFullName: startedByFullName,
    });
    startTaskHandler({ data: transformedVals });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues, taskDetails]);

  const checkIfEvidenceUploaded = useCallback(() => {
    // check if all the evidence are uploaded
    const allUploadedStatus = checkIfAllEvidencesAreUploaded();
    if (allUploadedStatus === TASK_STATUS.RESOLVED_INCOMPLETE.id) {
      setShowPopup(true); // show the popup
      return;
    }
    endTask();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues]);

  const transformEvidenceForApi = ({ taskDetails, formValues }) => {
    // there maybe evidence collected without saving first
    const evidenceList = formValues.evidenceCollection;
    if (isEmpty(taskDetails.evidence)) return null;
    return taskDetails.evidence.map(m => {
      const ele = find(evidenceList, { id: m.id });

      if (
        ele &&
        (ele.type === TASK_EVIDENCE_TYPE.OPTION_SINGLE.id ||
          ele.type === TASK_EVIDENCE_TYPE.OPTION_MULTIPLE.id)
      ) {
        return {
          ...m,
          completed: ele.completed.value,
          contents: toJSON(formatOptions(ele.contents.value.options)),
        };
      } else if (ele.type === TASK_EVIDENCE_TYPE.CHECKBOX.id) {
        return {
          ...m,
          completed: ele.completed.value,
          contents: null,
        };
      } else {
        return {
          ...m,
          completed: ele.completed.value,
          contents: ele.contents.value,
        };
      }
    });
  };

  const endTask = useCallback(() => {
    // get the latest data from formik
    const values = { ...formValues };
    // update the old evidence with new acquired evidence
    const indx = taskDetails.tasks.findIndex(f => f.id === values.id);
    let reqTask;
    if (indx !== -1) {
      reqTask = taskDetails.tasks[indx];
      reqTask = {
        ...reqTask, // extract the evidence collected
        evidence: transformEvidenceForApi({
          formValues: values,
          taskDetails: reqTask,
        }),
      };
    }

    const transformedVals = transformTaskFormValues({
      id: values.id,
      values,
      task: taskDetails,
      mobileStatus: checkIfAllEvidencesAreUploaded(),
      timeFinished: convertToUTCTime({
        date: Date.now(),
      }),
      finishedByID: startedByID,
      finishedByFullName: startedByFullName,
    });
    const transformedTaskDetails = {
      ...transformedVals,
      tasks: transformedVals.tasks.map(m => {
        if (m.id === reqTask.id) {
          return { ...m, evidence: reqTask.evidence };
        }
        return m;
      }),
    };
    endTaskHandler({ data: transformedTaskDetails });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues, taskDetails]);

  const getFormTitle = useCallback(() => {
    if (task?.status) {
      return `Perform Task(${TASK_STATUS[task.status].name})`;
    }
    return 'Perform Task';
  }, [task]);

  useEffect(() => {
    // automatically close the form if completed
    if (
      task.status === TASK_STATUS.RESOLVED_COMPLETE.id ||
      task.status === TASK_STATUS.RESOLVED_INCOMPLETE.id
    ) {
      typeof closeForm === 'function' && closeForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task]);

  return (
    <>
      {loading && (
        <Portal id={'portal'}>
          <CircularProgress />
        </Portal>
      )}
      <Portal id={'portal'}>
        <BaseForm
          size="portal"
          edit={false}
          imageRequired={false}
          onChange={handleFormValueUpdate}
          initialValues={initialValues}
          formTitle={getFormTitle()}
          closeForm={closeForm}
          onSubmit={onSubmit}
          operationalSection={operationalSectionFields({
            evidence: task.evidence,
            disabled: TASK_STATUS.NEW.id === task.status, // unless the start button is pressed, it should not enable evidence collection
            startTaskCb: () => {
              startTask();
            },
            endTaskCb: () => {
              checkIfEvidenceUploaded();
            },
            disableStartButton: !(TASK_STATUS.NEW.id === task.status), // this depends on the status of the task
            disableEndButton:
              TASK_STATUS.NEW.id === task.status ||
              TASK_STATUS.RESOLVED_COMPLETE.id === task.status ||
              TASK_STATUS.RESOLVED_INCOMPLETE.id === task.status,
          })}
          enableReinitialize={false}
          generalSection={generalSectionFields({
            services,
            view,
            userAssignedDepartments,
            securityCompanyId,
          })}
          detailedSection={detailedSectionFields({
            taskDetails,
            isRootTask: true,
            schedulingView: ['ONCE_OFF'],
            omitFields: ['generateQRCode'],
            editMode: false,
            surfaceImage: formValues.siteSurfaceImages
              ? formValues.siteSurfaceImages
              : GEOGRAPHICAL_MAP,
            location: formValues.location,
            selectedTaskType: formValues.taskType,
            siteEnabled: true,
          })}
          confirmPopup={{
            popupTitle: 'popup_title',
            popupStatusMessage: 'popup_evidence_status_message',
            popupActionMessage: 'popup_action_message',
            confirmBtnText: 'OK',
            cancelBtnText: 'Cancel',
            closePopupHandler: () => setShowPopup(false),
            cancelBtnHandler: () => {
              endTask(); // if cancel is pressed then end the task
              setShowPopup(false);
            },
            confirmBtnHandler: () => {
              setShowPopup(false);
            },
          }}
          showPopup={showPopup}
          disablePresist={true} // this will ensure that the values are not stored in local storage once the form is closed
        />
      </Portal>
    </>
  );
};
PerformTask.propTypes = {
  taskDetails: PropTypes.object,
  id: PropTypes.string,
  defaultValues: PropTypes.object,
  closeForm: PropTypes.func,
  view: PropTypes.string,
  startTaskHandler: PropTypes.func,
  endTaskHandler: PropTypes.func,
  completedEvidences: PropTypes.array,
};
export default PerformTask;
